import React from 'react';

import { getContactGender, getContactMainFeatures, getMainFeatureValue, getPlainDisplayValue } from '@libs/NwFeatures';

import { useFeaturesIndex } from '@hooks/API/UseFeatures';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import { getPublicMainFeature } from '@components/Features/FeaturesGroupUtils';
import NwIcon, { NwBoolIcon } from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

// lunghezza massima complessiva dei valori testuali di una feature nel riepilogo compatto
const MAX_VALUES_LENGTH = 30;

// numero massimo di feature dove il riepilogo non ha modo di scorrere (card, tabelle, wall)
const MAX_FEATURES = 8;

/** Contenuto del valore di una main feature, in base al tipo. */
const getMainFeatureContent = (mainFeature, featuresIndex, gender) => {
    const { T, C, V, DV } = mainFeature;
    if (C) {
        return getMainFeatureValue(mainFeature, featuresIndex, { gender });
    }
    if (T === 'Boolean') {
        return <NwBoolIcon value={String(V).toLowerCase() === 'true'} />;
    }
    if (T === 'Rating') {
        return Array(parseInt(V))
            .fill(null)
            .map((rating, key) => (
                <NwIcon key={`star-${key}`} icon={byPrefixAndName.fass['star']} style={{ fontSize: 11 }} />
            ));
    }
    return getPlainDisplayValue(DV);
};

const MainFeatures = ({ contact, empty, showAllFeatures, showPublicValues, highlightedFeatureIDs }) => {
    const filteredFeatures = getContactMainFeatures(contact);
    const featuresIndex = useFeaturesIndex();
    const breakpoints = useNwBreakPoints();
    // stessa metrica che mostra il drawer: senza il genere il riepilogo sceglierebbe USM/USW a caso
    const gender = getContactGender(contact);

    const isHighlighted = (mainFeature) => Boolean(highlightedFeatureIDs && highlightedFeatureIDs.has(mainFeature.ID));

    // le feature evidenziate sono quelle su cui si sta filtrando: vengono prima e non
    // subiscono i tagli del riepilogo compatto, altrimenti si evidenzierebbe il nulla
    const orderedFeatures =
        highlightedFeatureIDs && highlightedFeatureIDs.size
            ? [
                  ...filteredFeatures.filter((mainFeature) => isHighlighted(mainFeature)),
                  ...filteredFeatures.filter((mainFeature) => !isHighlighted(mainFeature)),
              ]
            : filteredFeatures;

    // valore privato e, dove presente, valore pubblico separato da bullet
    const renderValue = (mainFeature, publicMainFeature) => {
        if (!publicMainFeature) {
            return getMainFeatureContent(mainFeature, featuresIndex, gender);
        }
        return (
            <>
                {getMainFeatureContent(mainFeature, featuresIndex, gender)}
                <span className="feature-inline-separator">&bull;</span>
                <span className="public-feature-value">
                    {getMainFeatureContent(publicMainFeature, featuresIndex, gender)}
                </span>
            </>
        );
    };

    if (filteredFeatures.length) {
        return (
            <React.Fragment>
                {orderedFeatures.map((mainFeature, index) => {
                    // main feature dal payload .FT: N = nome, T = tipo, C = ConversionID, V = valore, DV = display value
                    const { N, T, C, DV } = mainFeature;
                    const highlighted = isHighlighted(mainFeature);
                    const featureClassName = highlighted ? 'feature-highlighted' : undefined;
                    if (!showAllFeatures && !highlighted && index >= MAX_FEATURES) {
                        return null;
                    }
                    const publicMainFeature = showPublicValues ? getPublicMainFeature(mainFeature) : null;
                    if (C) {
                        return (
                            <dl key={`feat${index}`} className={featureClassName}>
                                <dt>
                                    {N}
                                    {breakpoints === 1 && ':'}
                                </dt>
                                <dd>{renderValue(mainFeature, publicMainFeature)}</dd>
                            </dl>
                        );
                    }
                    if (T === 'Boolean' || T === 'Rating') {
                        return (
                            <dl key={`feat${index}`} className={featureClassName}>
                                <dt>{N}</dt>
                                <dd style={{ textAlign: 'center' }}>{renderValue(mainFeature, publicMainFeature)}</dd>
                            </dl>
                        );
                    }
                    // valori troppo lunghi non entrano nel riepilogo compatto: la feature viene saltata.
                    // Dove si mostrano tutte le feature non si taglia: c'è spazio per scorrere o impilare.
                    if (!showAllFeatures && !highlighted) {
                        const valuesLength =
                            getPlainDisplayValue(DV).length +
                            (publicMainFeature ? getPlainDisplayValue(publicMainFeature.DV).length : 0);
                        if (valuesLength > MAX_VALUES_LENGTH) {
                            return null;
                        }
                    }
                    return (
                        <dl key={`feat${index}`} className={featureClassName}>
                            <dt>
                                {N}
                                {breakpoints === 1 && ':'}
                            </dt>
                            <dd>{renderValue(mainFeature, publicMainFeature)}</dd>
                        </dl>
                    );
                })}
            </React.Fragment>
        );
    }
    return <React.Fragment>{empty ? empty : ''}</React.Fragment>;
};

export default MainFeatures;
